import { Box } from '@mui/material'
import { YearSlider } from 'components/common/RangeSlider/YearSlider'
import { AIOT_ODD_CASE } from 'constant'
import { useActions, useAppSelector } from 'hooks'
import { useContainerDimensions } from 'hooks/useContainerDimensions'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import { DataItem } from 'react-svg-worldmap'
import { Tooltip } from 'react-tooltip'
import { getCountryNameFromCountryLongCode } from 'utils'
import {
  formatNumberChart,
  formatNumberWorldMap,
  formatNumberChartStartOfRange,
  formatNumberDecimal,
} from 'utils/formatNumber'
import { ColorCard } from './WorldmapChart'

enum RANGECOLOR {
  NODATA = 'rgb(231, 231, 231)',
  ZERO = '#ddebf6',
  RANGE1 = 'rgb(116, 206, 237)',
  RANGE2 = 'rgb(58, 141, 199)',
  RANGE3 = 'rgb(212, 164, 203)',
  RANGE4 = 'rgb(157, 93, 163)',
  RANGE5 = 'rgb(77, 30, 82)',
  NEGATIVE = '#0d6b70',
}
export const AIOT_ODD_CASE_LEGENDS = ['Not released', 'In development', 'Released']
export const AIOT_ODD_CASE_LEGEND_LIST = {
  not_released: {
    label: 'Not released',
    color: RANGECOLOR.ZERO,
  },
  released: { label: 'Released', color: RANGECOLOR.RANGE1 },
  in_development: { label: 'In development', color: RANGECOLOR.RANGE2 },
}

interface ColorRange {
  color?: string
  rangeBreakpoints: number[]
  addToFirstStart?: number
  addToStart?: number
  lastEndOfRangeDecimal?: number
  innerEndNoRounding?: boolean
  innerStartNoAddition?: boolean
  innerStartDecimal?: number
  innerStartNoRounding?: boolean
  firstStartNegative?: boolean
  breakpoints?: number[]
  firstStartDecimal?: number
}
const geoUrl = 'data/feature.json'

interface SimpleWorldMapProps {
  data: DataItem<string | number>[]
  currentYear: number
  showPercentage: boolean
  downloadable?: boolean
  currentMetricRawName?: string
  sliderLimits?: {
    minYear: number
    maxYear: number
  }
}

const minNonZero = (array: number[]) =>
  array.filter((num) => num !== 0).reduce((min, current) => (current < min ? current : min), Infinity)

const getColorByRange = (input: any, range: number[]) => {
  const value = Number(input)
  if (isNaN(value)) return RANGECOLOR.NODATA
  if (value < 0) return RANGECOLOR.NEGATIVE
  if (value === 0) return RANGECOLOR.ZERO
  let color = RANGECOLOR.NODATA
  Array.from({ length: (range.length || 1) - 1 }).forEach((_, index) => {
    const condition =
      index === 0
        ? value >= range[index] && value <= range[index + 1]
        : value > range[index] && value <= range[index + 1]
    if (condition) {
      color = RANGECOLOR?.[`RANGE${index + 1}`]
    }
  })
  return color
}

const SimpleWorldMap: FC<SimpleWorldMapProps> = (props) => {
  const { data, showPercentage, currentYear, downloadable = false, currentMetricRawName, sliderLimits } = props
  const [content, setContent] = useState('')
  const { setWorldmapYear } = useActions()
  const legendData = useAppSelector(({ vibrancy }) => vibrancy.legendData)
  const legendYearData = useAppSelector(({ vibrancy }) => vibrancy.legendYearData)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const values = data.map((obj) => Number(obj.value)).filter((value) => !isNaN(value) && value !== null)
  const minDataValue = values.length ? Math.min(...values) : 0
  const maxDataValue = values.length ? Math.max(...values) : 0
  const containerRef = useRef(null)
  const { height } = useContainerDimensions(containerRef)
  const isOddCase = AIOT_ODD_CASE?.includes(currentMetricRawName as string)

  const getDynamicCountryColor = (value: number, minValue: number, maxValue: number): ColorRange => {
    let breakpoints: any = []
    const minValueNonZero = minNonZero(values)

    if (currentMetricRawName === 'relative_ai_skills_penetration') {
      if (maxValue > 2 && maxValue <= 3) {
        return {
          rangeBreakpoints: [0, 0.49, 1, 2, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          innerStartDecimal: 2,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
      if (maxValue > 1.2 && maxValue <= 1.5) {
        return {
          rangeBreakpoints: [0.01, 0.24, 0.49, 0.74, 1, 1.5],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
        }
      }
      if (maxValue > 1 && maxValue <= 1.2) {
        return {
          rangeBreakpoints: [0.01, 0.25, 0.5, 0.74, 1, 1.2],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
        }
      }
      if (maxValue > 1.5 && maxValue <= 2) {
        return {
          rangeBreakpoints: [0.01, 0.29, 0.59, 0.89, 1.19, maxValue],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerStartDecimal: 2,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
    }

    if (currentMetricRawName === 'academia_industry_model_production_concentration') {
      if (maxValue > 0.4 && maxValue <= 0.5 && minValue === 0) {
        return {
          rangeBreakpoints: [0.01, 0.19, 0.29, 0.39, maxDataValue],
          addToStart: 0.01,
        }
      }
      if (maxValue < 0.4 && minValue === 0) {
        return {
          rangeBreakpoints: [0.01, 0.09, 0.19, 0.29, maxDataValue],
          addToStart: 0.01,
        }
      }
    }
    if (currentMetricRawName === 'ai_talent_concentration') {
      if (maxValue > 0.9 && maxValue < 1) {
        return {
          rangeBreakpoints: [0.01, 0.19, 0.39, 0.59, 0.79, maxDataValue],
          addToStart: 0.01,
        }
      }
      if (maxValue > 0.5 && maxValue < 0.9) {
        return {
          rangeBreakpoints: [0.01, 0.09, 0.19, 0.29, 0.39, maxDataValue],
          addToStart: 0.01,
        }
      }
      if (maxValue > 1 && maxValue <= 1.2) {
        return {
          rangeBreakpoints: [0.01, 0.25, 0.5, 0.74, 1, 1.2],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
        }
      }
    }
    if (currentMetricRawName === 'ai_job_postings_perc_of_all_job_postings') {
      if (maxValue > 1 && maxValue <= 1.2) {
        return {
          rangeBreakpoints: [0.01, 0.24, 0.49, 0.74, 1, 1.2],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
        }
      }
      if (maxValue > 1.2 && maxValue <= 1.5) {
        return {
          rangeBreakpoints: [0.01, 0.24, 0.49, 0.74, 1, 1.5],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
        }
      }
      if (maxValue > 1.5 && maxValue <= 2) {
        return {
          rangeBreakpoints: [0.01, 0.29, 0.59, 0.89, 1.19, maxValue],
          addToStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
    }
    if (currentMetricRawName === 'ai_study_programs_in_english_percentage') {
      if (maxValue > 3 && maxValue <= 5) {
        return {
          rangeBreakpoints: [0, 1, 2, 3, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
      if (maxValue > 2 && maxValue <= 3) {
        return {
          rangeBreakpoints: [0, 0.49, 1, 2, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
      if (maxValue > 1 && maxValue <= 2) {
        return {
          rangeBreakpoints: [0, 0.29, 0.59, 1, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
    }
    if (currentMetricRawName === 'ai_talent_concentration_gender_equality_index') {
      if (maxDataValue > 0.8 && maxDataValue <= 1) {
        return {
          rangeBreakpoints: [0.01, 0.19, 0.39, 0.59, 0.79, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
      if (maxDataValue > 0.6 && maxDataValue <= 0.8) {
        return {
          rangeBreakpoints: [0.01, 0.14, 0.29, 0.44, 0.59, maxValue],
          addToStart: 0.01,
          addToFirstStart: 0.01,
          lastEndOfRangeDecimal: 1,
          innerEndNoRounding: true,
          innerStartNoAddition: true,
          innerStartNoRounding: true,
        }
      }
    }
    if (currentMetricRawName === 'num_ai_related_bills_passed') {
      if (maxValue === 3 && minDataValue === 0) {
        return {
          rangeBreakpoints: [0, 1, 2, 3],
          addToFirstStart: 1,
          addToStart: 1,
        }
      }
      if ((maxDataValue === 4 || maxDataValue === 5) && minDataValue === 0) {
        return {
          rangeBreakpoints: [0, 2, maxValue],
          addToFirstStart: 1,
          addToStart: 1,
        }
      }
    }
    if (currentMetricRawName === 'net_migration_flow_ai_skills_per_10k') {
      if (minValue < 0 && maxValue > 5) {
        return {
          rangeBreakpoints: [0, 2, 4, maxValue],
          lastEndOfRangeDecimal: 2,
          addToFirstStart: 0.01,
          addToStart: 0.01,
          innerStartDecimal: 2,
          firstStartNegative: true,
          firstStartDecimal: 2,
        }
      }
      if (minValue < 0 && maxValue > 3.5) {
        return {
          rangeBreakpoints: [0, 1, 2, maxValue],
          lastEndOfRangeDecimal: 2,
          addToFirstStart: 0.01,
          addToStart: 0.01,
          innerStartDecimal: 2,
          firstStartNegative: true,
        }
      }

      if (minValue < 0 && maxValue < 1) {
        return {
          rangeBreakpoints: [0, 0.39, 0.79, maxValue],
          addToFirstStart: 0.01,
          // innerStartNoAddition: true,
          addToStart: 0.01,
          firstStartNegative: true,
        }
      }
    }
    if (currentMetricRawName === 'relative_ai_hiring_rate_yoy_ratio') {
      if (minValue < 0 && maxValue > 20) {
        return {
          rangeBreakpoints: [0, 10, 20, maxValue],
          addToFirstStart: 0.01,
          // innerStartNoAddition: true,
          addToStart: 0.01,
          firstStartNegative: true,
        }
      }

      if (minValue < 0 && maxValue > 11 && maxValue <= 20) {
        return {
          rangeBreakpoints: [0, 5, 10, maxValue],
          addToFirstStart: 0.01,
          // innerStartNoAddition: true,
          addToStart: 0.01,
          firstStartNegative: true,
        }
      }
    }
    if (minValueNonZero === maxDataValue) {
      return {
        rangeBreakpoints: [minValueNonZero, maxDataValue],
      }
    }
    if (maxDataValue > 200 && maxDataValue <= 250) {
      return {
        rangeBreakpoints: minValue === 0 ? [0, 60, 120, 180, maxDataValue] : [0, 50, 100, 150, 200, maxDataValue],
      }
    }
    if (maxDataValue > 150 && maxDataValue <= 200) {
      return {
        rangeBreakpoints: minDataValue === 0 ? [0, 50, 100, 150, maxDataValue] : [0, 30, 60, 90, 120, maxDataValue],
      }
    }
    if (maxDataValue > 3000000000 && maxDataValue <= 4000000000) {
      return {
        // rangeBreakpoints: [0, 1000000000, 2000000000, 3000000000, 4000000000],
        rangeBreakpoints:
          minValue === 0
            ? [0, 1000000000, 2000000000, 3000000000, 4000000000]
            : [0, 500000000, 1000000000, 2500000000, 3000000000, 4000000000],
        innerEndNoRounding: true,
        innerStartNoAddition: true,
        innerStartNoRounding: true,
        addToStart: 0.1,
        lastEndOfRangeDecimal: 1,
        innerStartDecimal: 1,
      }
    }
    if (maxDataValue > 2200000000 && maxDataValue <= 3000000000) {
      return {
        rangeBreakpoints: [0, 1000000000, 2000000000, 3000000000],
      }
    }
    if (maxDataValue > 2000000000 && maxDataValue <= 2200000000) {
      return {
        rangeBreakpoints:
          minDataValue === 0
            ? [0, 500000000, 1000000000, 1500000000, 2200000000]
            : [0, 250000000, 500000000, 750000000, 1000000000, 2200000000],
        innerEndNoRounding: true,
        innerStartNoAddition: true,
        innerStartNoRounding: true,
        addToStart: 0.1,
        lastEndOfRangeDecimal: 1,
        innerStartDecimal: 1,
      }
    }
    if (maxDataValue > 1000000000 && maxDataValue <= 1200000000) {
      return {
        rangeBreakpoints:
          minDataValue === 0
            ? [0, 250000000, 500000000, 750000000, 1200000000]
            : [0, 250000000, 500000000, 750000000, 1000000000, 1200000000],
        lastEndOfRangeDecimal: 1,
      }
    }
    if (maxDataValue > 1200000000 && maxDataValue <= 2000000000) {
      return {
        rangeBreakpoints: [0, 250000000, 500000000, 750000000, 1000000000, maxDataValue],
        lastEndOfRangeDecimal: 1,
      }
    }
    if (maxDataValue > 1200000 && maxDataValue <= 1500000) {
      return {
        rangeBreakpoints: [minDataValue, 250000, 500000, 750000, 1000000, maxDataValue],
        lastEndOfRangeDecimal: 1,
      }
    }
    if (maxDataValue > 1000000 && maxDataValue <= 1200000) {
      return {
        rangeBreakpoints: [minDataValue, 250000, 500000, 750000, 1000000, 1200000],
        lastEndOfRangeDecimal: 1,
      }
    }
    if (maxValue < 0.005) {
      return {
        rangeBreakpoints:
          minValue === 0 && minValueNonZero > 0 ? [0, minValueNonZero, maxDataValue] : [minValue, maxDataValue],
      }
    }
    if (maxValue > 1 && maxValue <= 5) {
      return {
        rangeBreakpoints: [1, 5],
      }
    }
    if (minValue >= 1 && maxValue <= 2) {
      return {
        rangeBreakpoints: [1, 2],
      }
    }
    if (maxValue <= 20 && maxValue > 15) {
      return {
        color: value === 0 ? RANGECOLOR.ZERO : RANGECOLOR.RANGE1,
        // rangeBreakpoints: [minValueNonZero > 10 ? 1 : minValueNonZero, 10, maxValue],
        rangeBreakpoints: [0, 5, 10, maxValue],
      }
    }
    if (maxValue <= 21 && maxValue > 20) {
      return {
        color: value === 0 ? RANGECOLOR.ZERO : RANGECOLOR.RANGE1,
        rangeBreakpoints: [minValueNonZero > 10 ? 1 : minValueNonZero, 10, maxValue],
      }
    }
    if (maxValue <= 30 && maxValue > 20) {
      return {
        color: value === 0 ? RANGECOLOR.ZERO : RANGECOLOR.RANGE1,
        rangeBreakpoints: [minValueNonZero > 10 ? 1 : minValueNonZero, 10, 20, maxValue],
      }
    }

    if (maxValue <= 40 && maxValue > 30) {
      return {
        rangeBreakpoints: [minValueNonZero > 10 ? 1 : minValueNonZero, 10, 20, 30, maxValue],
      }
    }
    if (maxValue <= 50 && maxValue > 40) {
      return {
        rangeBreakpoints: [minValueNonZero > 10 ? 1 : minValueNonZero, 10, 20, 30, maxValue],
      }
    }
    if (maxValue <= 60 && maxValue > 50) {
      return {
        rangeBreakpoints:
          minValue === 0
            ? [0, 15, 30, 45, maxValue]
            : [minValueNonZero > 10 ? 1 : minValueNonZero, 10, 20, 30, 40, maxValue],
      }
    }
    if (maxValue <= 70 && maxValue > 60) {
      return {
        rangeBreakpoints:
          minValue === 0 ? [minValueNonZero, 20, 40, 60, maxValue] : [minValueNonZero, 15, 30, 45, 60, maxValue],
      }
    }
    if (maxValue <= 80 && maxValue > 70) {
      return {
        rangeBreakpoints:
          minValue === 0 ? [minValueNonZero, 20, 40, 60, maxValue] : [minValueNonZero, 15, 30, 45, 60, maxValue],
      }
    }

    if (maxValue <= 100 && maxValue >= 90) {
      breakpoints =
        minValue === 0
          ? [minValueNonZero > 10 ? 1 : minValueNonZero, 25, 50, 75, maxValue]
          : [minValueNonZero > 10 ? 1 : minValueNonZero, 20, 40, 60, 80, maxValue]
      return {
        color: getColorByRange(value, breakpoints),
        rangeBreakpoints: breakpoints,
      }
    }
    if (maxValue <= 100 && maxValue >= 80) {
      breakpoints = [minValueNonZero > 10 ? 1 : minValueNonZero, 20, 40, 60, 80, maxValue]
      return {
        color: getColorByRange(value, breakpoints),
        rangeBreakpoints: breakpoints,
      }
    }
    if (maxValue <= 10 && maxValue > 6) {
      return {
        color: value === 0 ? RANGECOLOR.ZERO : RANGECOLOR.RANGE1,
        rangeBreakpoints: [minValueNonZero, 5, maxValue],
      }
    }
    if (maxValue <= 15 && maxValue > 11) {
      return {
        color: value === 0 ? RANGECOLOR.ZERO : RANGECOLOR.RANGE1,
        rangeBreakpoints: [minValueNonZero, 5, 10, maxValue],
      }
    }

    const rangeSize = Math.floor((maxValue - minValue) / (minDataValue === 0 ? 4 : 5))
    const range1End = minValue + rangeSize
    const range2End = range1End + rangeSize
    const range3End = range2End + rangeSize
    const range4End = minDataValue === 0 ? maxValue : range3End + rangeSize
    const range5End = maxValue

    breakpoints = [minValue, range1End, range2End, range3End, range4End]
    if (minDataValue !== 0) {
      breakpoints.push(range5End)
    }
    if (Number.isNaN(value)) {
      return {
        color: RANGECOLOR.NODATA,
        rangeBreakpoints: breakpoints,
      }
    }
    return {
      color:
        value === 0 && range1End > 1
          ? RANGECOLOR.ZERO
          : value >= minValue
          ? value <= range1End
            ? RANGECOLOR.RANGE1
            : value <= range2End
            ? RANGECOLOR.RANGE2
            : value <= range3End
            ? RANGECOLOR.RANGE3
            : value <= range4End
            ? RANGECOLOR.RANGE4
            : RANGECOLOR.RANGE5
          : RANGECOLOR.NODATA,
      rangeBreakpoints: breakpoints,
    }
  }

  const getRangeColor = getDynamicCountryColor(0, minDataValue, maxDataValue)
  const colorRange = getRangeColor?.rangeBreakpoints
  const tooltipTextFunction = (context: { countryName: any; countryValue: any; status: any; legendYear: any }) => {
    if (isNaN(context?.countryValue)) return ''
    if (isOddCase) {
      return context?.status === 'not_released' ? '' : `${context.countryName}, ${context.legendYear}`
    }
    const parseNumber = Math.abs(Number(context?.countryValue))
    const prefix = Number(context?.countryValue) < 0 ? '-' : ''

    if (parseNumber < 0.00000001) {
      return `${context.countryName} ${prefix}${formatNumberChart(parseNumber)}${showPercentage ? '%' : ''}`
    }
    if ((parseNumber > 0.01 && parseNumber < 1) || (parseNumber < -0.01 && parseNumber > -1))
      return `${context.countryName} \n ${prefix}${parseNumber?.toFixed(2)}${showPercentage ? '%' : ''}`
    return showPercentage
      ? `${context.countryName} \n ${prefix}${formatNumberDecimal(parseNumber, 2)}%`
      : `${context.countryName} \n ${prefix}${formatNumberChart(parseNumber, 2).toString()}`
  }

  const decimals = useMemo(() => {
    return maxDataValue < 1000 ? 2 : 0
  }, [maxDataValue])

  const firstStartOfRange = () => {
    if (getRangeColor.firstStartNegative) {
      return '0.01'
    }
    if (colorRange[0] && colorRange[0] < 0.00000001) {
      return formatNumberWorldMap(colorRange[0])
    }
    const shouldFormat2Decimal =
      (colorRange[0] >= 0.01 && colorRange[0] < 1) || (colorRange[0] <= -0.01 && colorRange[0] > -1)

    if (shouldFormat2Decimal) {
      return Number(colorRange[0]).toFixed(2)
    }

    return showPercentage
      ? formatNumberDecimal(colorRange[0] + (getRangeColor?.addToFirstStart ?? 0), 2)
      : formatNumberWorldMap(
          colorRange[0] === 0 && colorRange[1] > 1 ? 1 : colorRange[0] + (getRangeColor.addToFirstStart ?? 0),
          decimals,
        ).toString()
  }

  const startOfRange = useCallback(
    (num: number) => {
      let tempNum = num
      if (Number(num) >= 1000000000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000000000) {
        if (getRangeColor.innerStartDecimal && getRangeColor.addToStart) {
          return `${roundToTenths(Number(num) / 1000000000) + getRangeColor.addToStart}B`
        }
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) >= 1000 && Number(num) < 1000000) {
        return formatNumberChartStartOfRange(num, 0, false, true).toString()
      } else if (Number(num) > 1 && Number(num) < 1000) {
        if (showPercentage) {
          return formatNumberDecimal(
            num + (getRangeColor?.addToStart ? getRangeColor?.addToStart : getRangeColor.innerStartNoAddition ? 0 : 1),
            2,
          )
        }
        return formatNumberWorldMap(
          tempNum +
            (getRangeColor?.addToStart ? getRangeColor?.addToStart : getRangeColor.innerStartNoAddition ? 0 : 1),
          showPercentage ? 2 : getRangeColor.innerStartDecimal ?? 0,
        ).toString()
      }

      return formatNumberWorldMap(tempNum + (getRangeColor?.addToStart || 0), decimals).toString()
    },
    [decimals, getRangeColor],
  )

  function roundToTenths(num: number): number {
    return Math.round(num * 10) / 10
  }
  const endOfRange = useCallback(
    (num: number) => {
      if (num >= 0.01 && num < 1) {
        return Number(num).toFixed(2)
      }
      if (getRangeColor.innerEndNoRounding) {
        if (Number(num) >= 1000000000) {
          return `${roundToTenths(Number(num) / 1000000000)}B`
        }
      }
      if (showPercentage) {
        return formatNumberDecimal(num, 2)
      }
      return formatNumberWorldMap(num, decimals).toString()
    },
    [decimals, getRangeColor],
  )
  const lastEndOfRange = useCallback(
    (num: number) => {
      if (num >= 0.01 && num < 1) {
        return Number(num).toFixed(2)
      }
      if (showPercentage) {
        return formatNumberDecimal(num, 2)
      }
      return formatNumberWorldMap(num, getRangeColor.lastEndOfRangeDecimal ?? 0).toString()
    },
    [decimals, getRangeColor],
  )

  const getDataByCode = (code: string) => {
    return data?.find((ele: any) => ele.countryLongCode === code) || null
  }

  const hasData = useMemo(() => {
    return !!values.length
  }, [values])

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ height: 'calc(100% - 120px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        marginTop={`${(height || 0) * -0.05}px`}
      >
        {hasData ? (
          <ComposableMap
            projectionConfig={{
              rotate: [-15, 0, 0],
              scale: 180,
            }}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }: any) =>
                geographies.map((geo: any) => {
                  const d = getDataByCode(geo.id)

                  const countryName = getCountryNameFromCountryLongCode(countryData, d?.countryLongCode)
                  const status = legendData?.[countryName as string]?.[currentYear]
                  const legendYear = legendYearData?.[countryName as string]

                  const tooltipContent = !d
                    ? ''
                    : tooltipTextFunction({
                        countryName: geo?.properties?.name,
                        countryValue: d?.value,
                        status,
                        legendYear,
                      })

                  const getColor = !isOddCase
                    ? getColorByRange(Number(d?.value), colorRange)
                    : AIOT_ODD_CASE_LEGEND_LIST?.[status]?.color
                  return (
                    <Geography
                      data-tooltip-id='world-map-tool-tip-id'
                      data-tooltip-offset={15}
                      key={geo.rsmKey}
                      geography={geo}
                      fill={d ? getColor : 'rgb(231, 231, 231)'}
                      onMouseEnter={() => {
                        setContent(tooltipContent)
                      }}
                      onMouseLeave={() => {
                        setContent('')
                      }}
                      style={{
                        hover: { stroke: 'blue', strokeOpacity: 0.4, outline: 'none' },
                        default: { stroke: 'blue', strokeOpacity: 0.1, outline: 'none' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  )
                })
              }
            </Geographies>
          </ComposableMap>
        ) : (
          <Box flex={1}>
            <span>No data available</span>
          </Box>
        )}
      </Box>
      <Tooltip float id='world-map-tool-tip-id' place='bottom-start' variant='dark'>
        {content}
      </Tooltip>
      <Box marginTop={`${(height || 0) * -0.1}px`}>
        {hasData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <ColorCard color={RANGECOLOR.NODATA} text='No data' />
            {getRangeColor.firstStartNegative ? (
              <ColorCard
                color={RANGECOLOR.NEGATIVE}
                minValue={`${minDataValue.toFixed(2)}${showPercentage ? '%' : ''}`}
                maxValue={`0${showPercentage ? '%' : ''}`}
              />
            ) : null}
            <>
              {(minDataValue === 0 && !isOddCase) || getRangeColor.firstStartNegative ? (
                <ColorCard color={RANGECOLOR.ZERO} text={`0${showPercentage ? '%' : ''}`} />
              ) : null}
              {isOddCase
                ? Object.values(AIOT_ODD_CASE_LEGEND_LIST)?.map((l: any, index: number) => (
                    <ColorCard color={l.color} text={l.label} />
                  ))
                : Array.from({ length: (colorRange?.length || 1) - 1 }).map((range, index) => (
                    <ColorCard
                      color={RANGECOLOR?.[`RANGE${index + 1}`]}
                      minValue={index === 0 ? firstStartOfRange() : startOfRange(colorRange[index])}
                      maxValue={
                        index === (colorRange?.length || 1) - 2
                          ? lastEndOfRange(colorRange[index + 1])
                          : endOfRange(colorRange[index + 1])
                      }
                      showPercentage={showPercentage}
                    />
                  ))}
            </>
          </Box>
        ) : null}
        {downloadable ? null : (
          <Box sx={{ mt: '30px', px: '30px' }}>
            <YearSlider
              value={currentYear}
              onChange={setWorldmapYear}
              sliderLimits={sliderLimits ? sliderLimits : { minYear: 2017, maxYear: 2023 }}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default SimpleWorldMap
